// Type2 Mock Data - Room View
// This is for Room View (SimpleTimetableGrid with rooms as rows)
// School: Hollins - Rows are 33 rooms
// Cell shows: Teacher, Class
// Popup shows: Subject and Class (title), Teacher, Capacity, Period/Lesson Date, Period/Lesson Timing

import type { CellData, RowData, Type1Data } from './type1MockData';

// Re-export types
export type { CellData, RowData, Type1Data as Type2Data };

// Subject colors - each subject has a unique color
const subjectColors: Record<string, string> = {
  Eng: '#10B981', // Green
  Mat: '#3B82F6', // Blue
  Sci: '#8B5CF6', // Purple
  DT: '#F59E0B', // Amber
  His: '#EF4444', // Red
  MFL: '#0EA5E9', // Sky
  Geo: '#EC4899', // Pink
  Art: '#6366F1', // Indigo
};

// Full subject names
const subjectFullNames: Record<string, string> = {
  Eng: 'English',
  Mat: 'Mathematics',
  Sci: 'Science',
  DT: 'Design Technology',
  His: 'History',
  MFL: 'Modern Foreign Languages',
  Geo: 'Geography',
  Art: 'Art',
};

// Teachers by subject with full names
interface TeacherInfo {
  initials: string;
  fullName: string;
  subject: string;
}

const allTeachers: TeacherInfo[] = [
  // English Teachers
  { initials: 'SHW', fullName: 'Mr S Howard', subject: 'Eng' },
  { initials: 'NTA', fullName: 'Ms N Taylor', subject: 'Eng' },
  { initials: 'SWA', fullName: 'Mrs S Walsh', subject: 'Eng' },
  // Maths Teachers
  { initials: 'JGA', fullName: 'Mr J Garcia', subject: 'Mat' },
  { initials: 'NBR', fullName: 'Mrs N Brown', subject: 'Mat' },
  { initials: 'DHA', fullName: 'Mr D Hall', subject: 'Mat' },
  // Science Teachers
  { initials: 'ECA', fullName: 'Dr E Carter', subject: 'Sci' },
  { initials: 'SBM', fullName: 'Mr S Bloom', subject: 'Sci' },
  // Design Teachers
  { initials: 'SSC', fullName: 'Mr S Scott', subject: 'DT' },
  { initials: 'EBA', fullName: 'Ms E Baker', subject: 'DT' },
  // History Teachers
  { initials: 'CWA', fullName: 'Mr C Watson', subject: 'His' },
  { initials: 'ASA', fullName: 'Ms A Sanders', subject: 'His' },
  // MFL Teachers
  { initials: 'CKI', fullName: 'Mrs C King', subject: 'MFL' },
  { initials: 'JGR', fullName: 'Mr J Green', subject: 'MFL' },
  { initials: 'HDO', fullName: 'Ms H Donovan', subject: 'MFL' },
  // Geography Teachers
  { initials: 'TDU', fullName: 'Mr T Duncan', subject: 'Geo' },
  { initials: 'NJO', fullName: 'Mrs N Jones', subject: 'Geo' },
  { initials: 'HUM', fullName: 'Dr H Humphrey', subject: 'Geo' },
  // Art Teachers
  { initials: 'LKE', fullName: 'Ms L Kelly', subject: 'Art' },
  { initials: 'PDR', fullName: 'Mr P Drew', subject: 'Art' },
  { initials: 'LWH', fullName: 'Mrs L White', subject: 'Art' },
];

// All rooms with capacities
interface RoomInfo {
  name: string;
  capacity: number;
}

const allRooms: RoomInfo[] = [
  { name: 'GYM', capacity: 120 },
  { name: 'Hall', capacity: 200 },
  { name: 'LRC', capacity: 60 },
  ...Array.from({ length: 30 }, (_, i) => ({
    name: `R${i + 1}`,
    capacity: 25 + Math.floor((i % 4) * 5), // Capacity varies between 25-40
  })),
];

// Year groups and their sets
const yearGroups = [7, 8, 9, 10, 11];
const bandXSets789 = ['H', 'O', 'L'];
const bandYSets789 = ['I', 'N', 'S'];
const bandXSets1011 = ['H', 'O', 'L', 'T'];
const bandYSets1011 = ['I', 'N', 'S', 'C'];

// Period times
const periodTimes = [
  { start: '09.05', end: '10.05' },
  { start: '10.05', end: '11.05' },
  { start: '11.20', end: '12.20' },
  { start: '13.00', end: '14.00' },
  { start: '14.00', end: '15.00' },
];

// Dates for January 2026 (Mon 5th - Fri 9th for Week A, Mon 12th - Fri 16th for Week B)
const weekDates: Record<string, Record<string, string>> = {
  A: {
    Monday: 'Mon 5th Jan 2026',
    Tuesday: 'Tue 6th Jan 2026',
    Wednesday: 'Wed 7th Jan 2026',
    Thursday: 'Thu 8th Jan 2026',
    Friday: 'Fri 9th Jan 2026',
  },
  B: {
    Monday: 'Mon 12th Jan 2026',
    Tuesday: 'Tue 13th Jan 2026',
    Wednesday: 'Wed 14th Jan 2026',
    Thursday: 'Thu 15th Jan 2026',
    Friday: 'Fri 16th Jan 2026',
  },
};

// Seeded random for consistency
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Helper to create cell key
const cellKey = (week: 'A' | 'B', day: string, period: number) => `week${week}-${day.toLowerCase()}-${period}`;

// Get a set letter based on year group and seed
const getSetLetter = (yearGroup: number, seed: number): string => {
  if (yearGroup <= 9) {
    const allSets = [...bandXSets789, ...bandYSets789];
    return allSets[Math.floor(seededRandom(seed) * allSets.length)];
  }
  const allSets = [...bandXSets1011, ...bandYSets1011];
  return allSets[Math.floor(seededRandom(seed) * allSets.length)];
};

// Helper to create a cell for Room View
// Cell shows: Teacher, Class
// Popup shows: Subject and Class (title), Teacher, Capacity, Period/Lesson Date, Period/Lesson Timing
const createCell = (
  id: string,
  subject: string,
  yearGroup: number,
  setLetter: string,
  room: RoomInfo,
  teacher: TeacherInfo,
  week: 'A' | 'B',
  day: string,
  period: number,
  isUnavailable = false,
): CellData => {
  const subjectName = subjectFullNames[subject] || subject;
  const color = subjectColors[subject] || '#94A3B8';
  const time = periodTimes[period - 1];
  const date = weekDates[week][day];

  // Cell label for Room View: Teacher, Class
  const label = `${teacher.initials}\n${yearGroup}${setLetter}`;

  return {
    id,
    label,
    color,
    isUnavailable,
    popupData: {
      title: `${subjectName} ${yearGroup}${setLetter}`,
      subtitle: subject,
      details: [
        { label: 'Teacher', value: teacher.fullName },
        { label: 'Capacity', value: `${room.capacity}` },
        { label: 'Date', value: date },
        { label: 'Time', value: `${time.start} - ${time.end}` },
      ],
    },
  };
};

// Generate rows with rooms as row titles
const generateRows = (): RowData[] => {
  const weeks: ('A' | 'B')[] = ['A', 'B'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periods = [1, 2, 3, 4, 5];

  return allRooms.map((room, roomIndex) => {
    const cells: Record<string, CellData | null> = {};

    weeks.forEach((week, weekIndex) => {
      days.forEach((day, dayIndex) => {
        periods.forEach((period) => {
          const key = cellKey(week, day, period);
          const seed = roomIndex * 1000 + weekIndex * 100 + dayIndex * 10 + period;

          // Randomly determine if this cell has a lesson (75% chance - rooms are often used)
          if (seededRandom(seed) > 0.25) {
            const yearGroup = yearGroups[Math.floor(seededRandom(seed + 1) * yearGroups.length)];
            const setLetter = getSetLetter(yearGroup, seed + 2);
            const teacher = allTeachers[Math.floor(seededRandom(seed + 3) * allTeachers.length)];
            const subject = teacher.subject;

            cells[key] = createCell(
              `${room.name}-${key}`,
              subject,
              yearGroup,
              setLetter,
              room,
              teacher,
              week,
              day,
              period,
              // Mark some cells as unavailable (about 3%)
              seededRandom(seed + 4) < 0.03,
            );
          } else {
            cells[key] = null; // Empty cell (room not in use)
          }
        });
      });
    });

    return {
      id: `row-${room.name}`,
      title: room.name,
      cells,
    };
  });
};

export const type2MockData: Type1Data = {
  rows: generateRows(),
  weeks: ['A', 'B'],
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  periods: [1, 2, 3, 4, 5],
};

export default type2MockData;
